import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

/* ===================================================== */

const AgentTimeZone = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("TimeZone/all", { agentId: parseInt(localStorage.getItem("userId")) })
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load time zones"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      ...values,
      agentId: parseInt(localStorage.getItem("userId")),
    };

    apiPost("TimeZone", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    apiPut("TimeZone", values)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`TimeZone/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    timeZoneTitle: Yup.string().required("Title required"),
    timeOffsetFromGMT: Yup.number().required("Offset required"),
  });

  /* ================= RENDER ================= */

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
        <Row className="mb-20">
            <Col xs="12">
                <Breadcrumb title="Settings" breadcrumbItem="Timezone" />
            </Col>
        </Row>
      {/* ============ ADD ============ */}
      <Row>
        <Col xl="12">
          <Card>
            <CardBody>
              

              <Formik
                initialValues={{
                  timeZoneTitle: "",
                  timeOffsetFromGMT: 0,
                  isDayLightSavingTime: false,
                  isActive: true,
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {() => (
                  <Form>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>Time Zone Title *</Label>
                          <SafeField
                            name="timeZoneTitle"
                            className="form-control"
                          />
                          <ErrorMessage
                            name="timeZoneTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Offset from GMT (minutes)</Label>
                          <SafeField
                            name="timeOffsetFromGMT"
                            type="number"
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2" className="">
                        <Button type="submit" color="primary" className="mt-4">
                          Save
                        </Button>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="3">
                        <FormGroup check>
                          <Field
                            type="checkbox"
                            name="isDayLightSavingTime"
                          />{" "}
                          <Label check>DST</Label>
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup check>
                          <Field type="checkbox" name="isActive" />{" "}
                          <Label check>Active</Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ============ LIST ============ */}
          <Card>
            <CardBody>
              <h5>Time Zone List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>GMT Offset</th>
                      <th>DST</th>
                      <th>Active</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.timeZoneId || i}>
                        <td>{item.timeZoneTitle}</td>
                        <td>{item.timeOffsetFromGMT}</td>
                        <td>{item.isDayLightSavingTime ? "Yes" : "No"}</td>
                        <td>{item.isActive ? "Yes" : "No"}</td>
                        <td>
                          <ActionEdit onClick={() => openEdit(item)} />{" "}
                        <ActionDelete onClick={() => handleDelete(item.id)} />
                          
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ============ EDIT ============ */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Time Zone</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {() => (
              <Form>
                <FormGroup>
                  <Label>Time Zone Title</Label>
                  <SafeField name="timeZoneTitle" className="form-control" />
                </FormGroup>

                <FormGroup>
                  <Label>Offset from GMT</Label>
                  <SafeField
                    name="timeOffsetFromGMT"
                    type="number"
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup check className="mb-2">
                  <Field
                    type="checkbox"
                    name="isDayLightSavingTime"
                  />{" "}
                  <Label check>DST</Label>
                </FormGroup>

                <FormGroup check className="mb-3">
                  <Field type="checkbox" name="isActive" />{" "}
                  <Label check>Active</Label>
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default AgentTimeZone;
